import React, { useState } from 'react';
import selectStyles from '../Select/Select.module.css';
import searchStyles from '../SearchSelect/SearchSelect.module.css';
import useMenuSuspenso from '../SearchSelect/useMenuSuspenso';
import styles from './MultiSelect.module.css';

const normalizar = (texto) => String(texto ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export default function MultiSelect({
    label,
    value = [],
    options,
    onChange,
    disabled,
    error,
    helperText,
    placeholder = '',
    searchable = false,
    searchPlaceholder = 'Buscar...',
    selectAllLabel,
    noOptionsText = 'Nenhuma opção encontrada',
    className,
}) {
    const { aberto, setAberto, wrapperRef } = useMenuSuspenso();
    const [termo, setTermo] = useState('');

    const habilitadas = options.filter((opcao) => !opcao.disabled);
    const valoresHabilitados = [...new Set(habilitadas.map((opcao) => opcao.value))];
    const selecionadas = options.filter((opcao, indice) => value.includes(opcao.value) && options.findIndex((outra) => outra.value === opcao.value) === indice);
    const todasSelecionadas = valoresHabilitados.length > 0 && valoresHabilitados.every((valor) => value.includes(valor));
    const rotuloElevado = aberto || selecionadas.length > 0 || Boolean(placeholder);
    const visiveis = searchable && termo
        ? options.filter((opcao) => normalizar(`${opcao.group ?? ''} ${opcao.label}`).includes(normalizar(termo)))
        : options;

    const resumo = () => {
        if (selecionadas.length === 0) return placeholder;
        if (todasSelecionadas && selectAllLabel) return selectAllLabel;
        return selecionadas.map((opcao) => opcao.label).join(', ');
    };

    const alternarMenu = () => {
        if (!aberto) setTermo('');
        setAberto(!aberto);
    };

    const alternarOpcao = (opcao) => {
        if (opcao.disabled) return;
        const proximos = value.includes(opcao.value)
            ? value.filter((item) => item !== opcao.value)
            : [...value, opcao.value];
        onChange(proximos);
    };

    const alternarTodas = () => onChange(todasSelecionadas ? [] : valoresHabilitados);

    const alternarGrupo = (grupo) => {
        const valoresDoGrupo = [...new Set(habilitadas.filter((opcao) => opcao.group === grupo).map((opcao) => opcao.value))];
        const grupoMarcado = valoresDoGrupo.length > 0 && valoresDoGrupo.every((valor) => value.includes(valor));
        onChange(grupoMarcado
            ? value.filter((valor) => !valoresDoGrupo.includes(valor))
            : [...new Set([...value, ...valoresDoGrupo])]);
    };

    return (
        <div className={`${selectStyles.wrapper} ${className || ''}`} ref={wrapperRef}>
            <button
                type="button"
                role="combobox"
                aria-expanded={aberto}
                aria-multiselectable="true"
                disabled={disabled}
                className={`${selectStyles.campo} ${aberto ? selectStyles.campoAberto : ''} ${error ? selectStyles.campoComErro : ''}`}
                onClick={alternarMenu}
                onKeyDown={(evento) => evento.key === 'Escape' && setAberto(false)}
            >
                <span className={selectStyles.valor}>{resumo()}</span>
                <i className={`fa fa-chevron-down ${selectStyles.seta} ${aberto ? selectStyles.setaAberta : ''}`} />
            </button>

            {label && (
                <span className={`${selectStyles.rotulo} ${rotuloElevado ? selectStyles.rotuloElevado : ''} ${error ? selectStyles.rotuloComErro : ''}`}>
                    {label}
                </span>
            )}

            {aberto && (
                <div className={selectStyles.menu} role="listbox" aria-multiselectable="true">
                    {searchable && (
                        <div className={searchStyles.busca}>
                            <input
                                autoFocus
                                type="text"
                                className={searchStyles.buscaInput}
                                placeholder={searchPlaceholder}
                                value={termo}
                                onChange={(evento) => setTermo(evento.target.value)}
                            />
                        </div>
                    )}

                    {selectAllLabel && !termo && (
                        <button
                            type="button"
                            role="option"
                            aria-selected={todasSelecionadas}
                            className={`${selectStyles.opcao} ${searchStyles.opcaoMulti} ${todasSelecionadas ? selectStyles.opcaoAtiva : ''}`}
                            onClick={alternarTodas}
                        >
                            <span>{selectAllLabel}</span>
                            {todasSelecionadas && <i className={`fa fa-check ${searchStyles.marca}`} />}
                        </button>
                    )}

                    {visiveis.length === 0 && <p className={searchStyles.vazio}>{noOptionsText}</p>}

                    {visiveis.map((opcao, indice) => {
                        const marcada = value.includes(opcao.value);
                        const iniciaGrupo = opcao.group && opcao.group !== visiveis[indice - 1]?.group;
                        return (
                            <React.Fragment key={`${opcao.group ?? ''}-${String(opcao.value)}`}>
                                {iniciaGrupo && (
                                    <button type="button" className={styles.grupo} onClick={() => alternarGrupo(opcao.group)}>
                                        <span>{opcao.group}</span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={marcada}
                                    aria-disabled={Boolean(opcao.disabled)}
                                    disabled={opcao.disabled}
                                    className={`${selectStyles.opcao} ${searchStyles.opcaoMulti} ${marcada ? selectStyles.opcaoAtiva : ''} ${opcao.group ? styles.opcaoIndentada : ''} ${opcao.disabled ? styles.opcaoDesabilitada : ''}`}
                                    onClick={() => alternarOpcao(opcao)}
                                >
                                    {opcao.description
                                        ? <span className={styles.rotuloComDescricao}><span>{opcao.label}</span><span className={styles.descricao}>{opcao.description}</span></span>
                                        : <span>{opcao.label}</span>}
                                    {marcada && <i className={`fa fa-check ${searchStyles.marca}`} />}
                                </button>
                            </React.Fragment>
                        );
                    })}
                </div>
            )}

            {helperText && <p className={`${selectStyles.ajuda} ${error ? selectStyles.ajudaComErro : ''}`}>{helperText}</p>}
        </div>
    );
}
